import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { submittalsRegistryListInfo } from '../../../../../../core/projects.selectors';
import { FetchSubmittalsRegistryListRequest, UploadSubmittalLogFileRequest } from '../../../../../../core/projects.actions';
import {
  CreateOrEditSubmittalsRegistryComponent,
} from '../create-or-edit-submittals-registry/create-or-edit-submittals-registry.component';
import { FormControl, FormGroup } from '@angular/forms';
import { SubmittalsService } from '../submittals.service';

@Component({
    selector: 'app-submittals-registry',
    templateUrl: './submittals-registry.component.html',
    styleUrls: ['./submittals-registry.component.scss'],
    standalone: false
})
export class SubmittalsRegistryComponent implements OnInit, OnDestroy {
  @ViewChild('csvFileUploadRef', { static: false }) csvFileUploadRef: any;
  @ViewChild('searchInput') searchInput: any;
  @ViewChild('sortSelect') sortSelect: any;
  isSearchFocused = false;
  form = new FormGroup({
    searchText: new FormControl(''),
  });
  searchText = '';
  displayedColumns: string[] = ['sectionNo', 'subSectionNo', 'createdBy', 'title'];
  sortByColumns: string[] = ['Section no', 'Sub section no', 'Created by', 'Title'];
  dataSource: any[] = [];
  filteredDataSource: any[] = [];
  originalDataSource: any[] = [];
  acceptedFileTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ];
  file: any;
  projectId: string;
  selectedRow: any;
  totalRecords = 0;

  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };

  currentPage = 1;
  itemsPerPage = 10;
  selectedSortColumn: string = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  private readonly onDestroy = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private submittalsService: SubmittalsService,
    private cdRef: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    }

    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(qp => {
      if (qp.tab === 'submittals' && qp.subTab === 'registry') {
        this.fetchSubmittalsRegistry();
      }
    });

    this.form.controls.searchText.valueChanges.pipe(debounceTime(200), takeUntil(this.onDestroy)).subscribe(data => {
      this.searchText = data || '';
      this.applyFilters();
    });

    this.store
      .select(submittalsRegistryListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.dataSource = [];
        this.originalDataSource = [];
        this.filteredDataSource = [];
        this.totalRecords = 0;

        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            this.originalDataSource = data?.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;

            this.applyFilters();
          }
        }

        this.cdRef.detectChanges();
      });

    this.commonService.clearRowSelection.pipe(takeUntil(this.onDestroy)).subscribe(() => (this.selectedRow = null));
  }

  fetchSubmittalsRegistry(): void {
    const payload = {
      start: this.pagination.pageIndex * this.pagination.pageSize,
      total: 1000,
      include: ['createdBy'],
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchSubmittalsRegistryListRequest({ payload }));
    }
  }

  rowClicked(row: any): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: CreateOrEditSubmittalsRegistryComponent,
      data: { ...row },
    });
  }

  create(): void {
    this.projectsService.openRightPanel.next({
      component: CreateOrEditSubmittalsRegistryComponent,
      data: {},
    });
  }

  upload() {
    this.projectsService.closeRightPanel.next();
    this.csvFileUploadRef.nativeElement.click();
  }

  handleCsvFileUpload() {
    this.file = this.csvFileUploadRef.nativeElement.files[0];
    if (this.file) {
      if (!this.acceptedFileTypes.includes(this.file?.type)) {
        this.commonService.notification('Invalid file type.', 'danger');
        return;
      }
    }

    const payload = {
      inputFile: this.file,
      projectId: this.projectId,
    };

    if (this.file !== undefined) {
      this.store.dispatch(UploadSubmittalLogFileRequest({ payload }));
    }

    this.csvFileUploadRef.nativeElement.value = '';
  }

  onPageChanged(page: number): void {
    this.projectsService.closeRightPanel.next();
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  search() {
    const { searchText } = this.form.getRawValue();
    this.submittalsService.search.next(searchText);
  }

  close() {
    this.form.reset();
    const searchText = '';
    this.isSearchFocused = false;
    this.submittalsService.search.next(searchText);
  }

  applyFilters() {
    let filteredData = [...this.originalDataSource];
    if (this.searchText.trim()) {
      const search = this.searchText.trim().toLowerCase();
      filteredData = filteredData.filter(
        data =>
          data.description?.toLowerCase().includes(search) ||
          data.title?.toLowerCase().includes(search) ||
          data.section_no?.toLowerCase().includes(search) ||
          data.sub_section_no?.toLowerCase().includes(search),
      );
    }

    filteredData = this.sortItems(filteredData);

    this.filteredDataSource = filteredData;
    this.totalRecords = filteredData.length;
    this.paginateData(true);
  }

  onSearchFocus() {
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    this.isSearchFocused = true;
  }

  openSortDropdown(event?: Event): void {
    // Material renders the select panel and its backdrop as an inline popover
    // *inside* this wrapper, so the click that closes the panel bubbles back
    // here and would reopen it. Only react to clicks on the button itself.
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.cdk-overlay-popover, .cdk-overlay-backdrop')) return;
    this.sortSelect?.open?.();
  }

  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event.value;
    this.sortDirection = 'asc';
    this.applyFilters();
  }

  removeSort(event: Event): void {
    event?.stopPropagation();
    this.selectedSortColumn = null;
    this.sortDirection = 'asc';
    this.applyFilters();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.applyFilters();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private sortItems(items: any[]): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const itemsToSort = [...items];

    const safeLower = (v: any) => String(v ?? '').toLowerCase();
    const safeDate = (v: any) => {
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    if (!this.selectedSortColumn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const sortFns: Record<string, (a: any, b: any) => number> = {
      'Section no': (a, b) => safeLower(a.section_no).localeCompare(safeLower(b.section_no)),
      'Sub section no': (a, b) => safeLower(a.sub_section_no).localeCompare(safeLower(b.sub_section_no)),
      'Created by': (a, b) => {
        const nameA = `${a?.createdBy?.first_name ?? ''} ${a?.createdBy?.last_name ?? ''}`.trim();
        const nameB = `${b?.createdBy?.first_name ?? ''} ${b?.createdBy?.last_name ?? ''}`.trim();
        return safeLower(nameA).localeCompare(safeLower(nameB));
      },
      Title: (a, b) => safeLower(a.title).localeCompare(safeLower(b.title)),
    };

    const fn = sortFns[this.selectedSortColumn];
    if (!fn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const applyDirection = (result: number) => (this.sortDirection === 'asc' ? result : -result);

    return itemsToSort.sort((a, b) => {
      const primary = fn(a, b);
      if (primary !== 0) {
        return applyDirection(primary);
      }
      return safeDate(b.updated_at) - safeDate(a.updated_at);
    });
  }
}
